/**
 * Base service class for Shopify operations
 * Contains shared utilities and common functionality
 */

import { IShopifyStorefrontClient } from "../interface";
import { IOptionsRequest } from "../../../models";
import { createDomainLogger } from "../../../logger";

export abstract class BaseShopifyService {
  protected client: IShopifyStorefrontClient;
  protected logger: ReturnType<typeof createDomainLogger>;

  constructor(
    client: IShopifyStorefrontClient,
    logger?: ReturnType<typeof createDomainLogger>
  ) {
    this.client = client;
    this.logger = logger || createDomainLogger("shopify", "shopify");
  }

  /**
   * Helper to extract Shopify userErrors from the data object
   */
  protected findUserErrors(response: any): any[] {
    const errors: any[] = [];

    const search = (obj: any) => {
      if (!obj || typeof obj !== "object") return;
      for (const key of Object.keys(obj)) {
        if (
          key === "userErrors" &&
          Array.isArray(obj[key]) &&
          obj[key].length > 0
        ) {
          errors.push(...obj[key]);
        } else if (typeof obj[key] === "object") {
          search(obj[key]);
        }
      }
    };

    search(response);
    return errors;
  }

  /**
   * Wrapper for the GraphQL query method
   * @param query GraphQL query
   * @param variables Query variables
   * @param options Request options (headers, etc.)
   * @returns Query result
   */
  protected async query<T = any>(
    query: string,
    variables?: Record<string, any>,
    options?: IOptionsRequest
  ): Promise<{ result?: T; errors?: any[] }> {
    try {
      const response = await this.client.request(query, {
        variables,
        ...(options?.headers && { headers: options.headers }),
      });

      // Check for Shopify userErrors inside the data
      const userErrors = this.findUserErrors(response);
      if (userErrors.length > 0) {
        return { result: response, errors: userErrors };
      }

      return { result: response };
    } catch (error: any) {
      // Handle GraphQL or network errors
      if (error.response?.errors) {
        return {
          result: error.response.data,
          errors: error.response.errors,
        };
      }

      // Generic fallback for network / unexpected errors
      return {
        errors: [
          {
            message:
              error.message ||
              "An unexpected error occurred while calling Shopify API.",
          },
        ],
      };
    }
  }
}
